import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { STALE_TIME } from '@/api/staleTimes';

/** The PCMP permission codes the API grants (`PcmpPermission`). */
export const PCMP_PERMISSION = {
  VIEW: 'pcmp:view',
  VIEW_ALL: 'pcmp:view-all',
  ACT: 'pcmp:act',
  EXPORT: 'pcmp:export',
  MIS: 'pcmp:mis',
  ACCOUNTS: 'pcmp:accounts',
  REQUEST_CREATE: 'pcmp:request:create',
  REQUEST_APPROVE: 'pcmp:request:approve',
  REQUEST_ACT: 'pcmp:request:act',
  CORRECTION: 'pcmp:correction',
  EDIT: 'pcmp:edit',
  EDIT_ANY_STAGE: 'pcmp:edit-any-stage',
  ADMIN: 'pcmp:admin',
  PROJECT_MANAGE: 'pcmp:project:manage',
} as const;

export type PcmpPermission = (typeof PCMP_PERMISSION)[keyof typeof PCMP_PERMISSION];

const pcmpAccessSchema = z.object({
  userId: z.coerce.number(),
  isAdmin: z.boolean(),
  isReadOnly: z.boolean(),
  isMisStaff: z.boolean(),
  isAccountsStaff: z.boolean(),
  // Kept as plain strings: codes this client does not know are ignored.
  permissions: z.array(z.string()),
});

export type PcmpAccess = z.infer<typeof pcmpAccessSchema>;

export const pcmpAccessQueryKeys = {
  me: () => ['pcmp-access', 'me'] as const,
};

export const pcmpAccessQueryOptions = () =>
  queryOptions({
    queryKey: pcmpAccessQueryKeys.me(),
    queryFn: async (): Promise<PcmpAccess> => {
      const res = await apiClient.get(API.pcmpAccess.me);
      return pcmpAccessSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });
